import { useLazyQuery, useMutation } from "@apollo/client";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import MultiStepForm from "@/components/MultiStepForm";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate } from "@/router";

import { CREATE_FACILITY_MUTATION } from "../mutations";
import { FACILITIES_SEARCH_QUERY, type FacilitySearchResult } from "../queries";
import {
  compactCreateFacilityInput,
  CreateFacilitySchema,
  isCourseTypeRequired,
  type CreateFacilityFormInitialValues,
  type CreateFacilityFormSubmitValues,
  type CreateFacilityFormValues,
  getCreateFacilityFormValues
} from "../_utils/createFacilityForm";
import { buildDuplicateSearchInputs, matchesDuplicateFacility } from "../_utils/facilityDuplicateCheck";

import CreateFacilityAlerts from "./createFacilitySteps/CreateFacilityAlerts";
import CourseDetailsStep from "./createFacilitySteps/CourseDetailsStep";
import FacilityInformationStep from "./createFacilitySteps/FacilityInformationStep";
import LocationContactStep from "./createFacilitySteps/LocationContactStep";
import SectionAffiliationStep from "./createFacilitySteps/SectionAffiliationStep";

interface CreateFacilityFormProps {
  initialValues?: CreateFacilityFormInitialValues;
}

export default function CreateFacilityForm({ initialValues }: CreateFacilityFormProps) {
  const navigate = useNavigate();
  const [createFacility] = useMutation(CREATE_FACILITY_MUTATION);
  const [findFacilities] = useLazyQuery(FACILITIES_SEARCH_QUERY, {
    fetchPolicy: "network-only"
  });
  const form = useForm<CreateFacilityFormValues>({
    defaultValues: getCreateFacilityFormValues(initialValues),
    resolver: zodResolver(CreateFacilitySchema),
    mode: "onChange"
  });
  const {
    formState: { isValid },
    watch,
    setValue
  } = form;
  const selectedSectionId = watch("sectionId");
  const selectedFacilityType = watch("facilityType");
  const courseTypeRequired = isCourseTypeRequired(selectedFacilityType);
  const previousSectionIdRef = useRef(selectedSectionId);
  const [isCreating, setIsCreating] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [duplicateCandidates, setDuplicateCandidates] = useState<FacilitySearchResult[]>([]);
  const [duplicateCheckError, setDuplicateCheckError] = useState("");
  const needsDuplicateAcknowledgement = duplicateCandidates.length > 0 || !!duplicateCheckError;

  useEffect(() => {
    if (previousSectionIdRef.current !== selectedSectionId) {
      setValue("chapterId", "");
      previousSectionIdRef.current = selectedSectionId;
    }
  }, [selectedSectionId, setValue]);

  useEffect(() => {
    const subscription = form.watch(() => {
      setDuplicateCandidates((current) => (current.length > 0 ? [] : current));
      setDuplicateCheckError((current) => (current ? "" : current));
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const alerts = (
    <CreateFacilityAlerts
      bannerError={bannerError}
      duplicateCandidates={duplicateCandidates}
      duplicateCheckError={duplicateCheckError}
    />
  );

  const steps = [
    {
      headline: "Facility Information",
      supportingText: "Step 1",
      content: (
        <>
          {alerts}
          <FacilityInformationStep />
        </>
      ),
      requiredFields: ["name", "facilityType", "operatingStatus", "website"]
    },
    {
      headline: "Location & Contact",
      supportingText: "Step 2",
      content: (
        <>
          {alerts}
          <LocationContactStep />
        </>
      ),
      requiredFields: [
        "mainAddress.address1",
        "mainAddress.city",
        "mainAddress.state",
        "mainAddress.postalCode",
        "mainAddress.countryCode",
        "phone"
      ]
    },
    {
      headline: "Section Affiliation",
      supportingText: "Step 3",
      content: (
        <>
          {alerts}
          <SectionAffiliationStep initialPostalCode={initialValues?.mainAddress?.postalCode} />
        </>
      ),
      requiredFields: ["sectionId"]
    },
    {
      headline: "Course Details",
      supportingText: "Step 4",
      content: (
        <>
          {alerts}
          <CourseDetailsStep />
        </>
      ),
      requiredFields: courseTypeRequired ? ["courseType", "operationType"] : ["operationType"]
    }
  ];

  const handleSubmit = async (rawFields: CreateFacilityFormValues) => {
    if (isCreating) return;

    setIsCreating(true);
    setBannerError("");

    try {
      const parsed = CreateFacilitySchema.safeParse(rawFields);
      if (!parsed.success) {
        setBannerError("Form validation failed. Review the form and try again.");
        setIsCreating(false);
        return;
      }
      const fields = parsed.data;
      if (!needsDuplicateAcknowledgement) {
        try {
          const searchInputs = buildDuplicateSearchInputs(fields);
          const results = await Promise.all(
            searchInputs.map((input) =>
              findFacilities({
                variables: {
                  first: 10,
                  input
                }
              })
            )
          );
          const candidatesById = new Map<string, FacilitySearchResult>();

          results
            .flatMap((result) => result.data?.facilities.nodes || [])
            .filter((candidate) => matchesDuplicateFacility(candidate, fields))
            .forEach((candidate) => candidatesById.set(candidate.id, candidate));

          const candidates = Array.from(candidatesById.values());
          if (candidates.length > 0) {
            setDuplicateCandidates(candidates);
            setIsCreating(false);
            return;
          }
        } catch {
          setDuplicateCheckError("Duplicate check could not be completed. Review carefully before saving.");
          setIsCreating(false);
          return;
        }
      }

      const { data } = await createFacility({
        variables: {
          input: compactCreateFacilityInput(fields as CreateFacilityFormSubmitValues)
        }
      });
      const { facility, message, success } = data?.createFacility || {};

      if (success && facility?.id) {
        showSnackbar(message || "Facility created", { close: true });
        navigate("/facilities/:facilityId/details/contact", {
          params: { facilityId: facility.id }
        });
        return;
      }

      throw new Error(message || "Something went wrong");
    } catch (error) {
      setBannerError((error as Error).message);
      setIsCreating(false);
    }
  };

  const isFormValid = isValid;
  const submitText = needsDuplicateAcknowledgement ? "Save Anyway" : "Save";

  return (
    <div data-testid="create-facility-form">
      <MultiStepForm
        form={form}
        steps={steps}
        renderStepper={true}
        validatePathOnStepClick={true}
        onSubmit={handleSubmit}
        backLink={{ to: "/facilities", title: "Cancel" }}
        modelName="Facility"
        customHeadline="Create Facility"
        submitText={submitText}
        submittingText="Creating Facility..."
        validationSchema={CreateFacilitySchema}
        showActions={true}
        disabledSubmit={!isFormValid || isCreating}
        isSubmitting={isCreating}
        submitDialogContent={
          <p className="text-body-medium">
            Are you sure you want to create this facility and add it to the PGA directory?
          </p>
        }
      />
    </div>
  );
}
